import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { formatFullDate, formatTime, meridiem, shiftEnd, today, toMinutes } from '../api/calendar'
import { moveMyClassNight } from '../api/teacher'
import RoomPicker from './RoomPicker'
import SchedulingAssistant from './SchedulingAssistant'

/**
 * One night of a class, moved to another day or another hour (D-698, D-700;
 * pilot item 24).
 *
 * <b>`asked`</b>: *"Teachers need to move classes. When changing a
 * solo/duo/trio they need to move sessions flexibly - one-offs or the whole
 * series."* The series is the class's own schedule and stays `ManageClasses`;
 * this is the one-off, which had no path at all — a solo whose dancer cannot
 * make one Tuesday could only be cancelled.
 *
 * <b>Where a teacher already stands.</b> It opens inside the roster she taps
 * on her schedule, with the names in front of her, rather than on a screen she
 * would have to go and find — the same argument that put the tape measure here
 * (D-440).
 *
 * <b>It shows the rooms, and it once did not</b> (D-893, amending D-700).
 *
 * D-700 withheld the choice: the night kept the room it was in, on the
 * reasoning that where a class meets is the studio's to arrange. What that left
 * her with was a <i>dead end</i> — `asked` 2026-10-13, moving a solo from a
 * Tuesday to a Monday: her Tuesday room is somebody else's on Monday, the
 * server refused with <i>"that room is already taken at that time"</i>, and
 * nothing on screen said which rooms were free or let her take one. The owner:
 * <i>"It should probably show all the rooms availability like scheduling a
 * private."</i>
 *
 * <b>Answered by the same function the booking form asks</b>
 * (`roomAvailability`, D-396), never a second opinion about whether a room is
 * free — and read for the day and hour she is moving <i>to</i>, which is the
 * question, rather than the one it is on now.
 */
export default function MoveNightPanel({
  classId,
  occurrenceId,
  date,
  startTime,
  endTime,
  roomId,
  dancerIds,
  teachingStaffIds,
  onDone,
}: {
  classId: string
  occurrenceId: string
  date: string
  startTime: string | null
  endTime: string | null
  /** Where it meets now, so the picker can mark it and default to it (D-893, D-896). */
  roomId: string | null
  /**
   * Everybody on the register, which is what decides whether a week grid is
   * worth drawing (D-898).
   *
   * ⚠ **Counted rather than read off `IsSoloDuetTrio`**, and D-665 is why: that
   * flag went from 86 classes to 2 with nothing failing, and the lesson written
   * down afterwards was to gate on a dancer count instead of trusting it.
   */
  dancerIds: string[]
  /** Who is teaching that night, ids only, for the free/busy lookup (D-898). */
  teachingStaffIds: string[]
  onDone: () => void
}) {
  /*
   * `<input type="time">` wants `HH:mm`, and these arrive as `HH:mm:ss` from a
   * `TimeOnly`. Sliced rather than parsed: anything else would be a second
   * opinion about a format the server owns.
   */
  const was = { day: date, start: startTime?.slice(0, 5) ?? '', end: endTime?.slice(0, 5) ?? '' }

  const [day, setDay] = useState(was.day)
  const [start, setStart] = useState(was.start)
  const [end, setEnd] = useState(was.end)

  const queryClient = useQueryClient()

  /*
   * <b>The day she is moving it to, not the one it is on</b> (D-893). Every
   * query below is keyed on it, so changing the date re-asks — which is the
   * whole point: a room free on Tuesday says nothing about Monday.
   */
  const landing = day || was.day
  const from = start || was.start
  const to = end || was.end

  /*
   * <b>Nothing to ask when there are no hours.</b> A class with no start time
   * meets on no dates (D-84), and `roomAvailability` wants a span.
   */
  const askable = Boolean(landing && from && to && to > from)

  /*
   * <b>When finding a time everyone can make is a question worth asking</b>
   * (D-898). `asked`: *"the move is primarily going to be utilized for solo duo
   * trios... but then the full sized classes we probably wouldn't cause it
   * would be trying to find a spot on say 17 dancers aschedules that works."*
   *
   * <b>Three dancers, counted off the register</b> — not read off
   * `IsSoloDuetTrio`, which went from 86 classes to 2 with nothing failing
   * (D-665). A count cannot drift from the thing it counts.
   *
   * <b>And exactly one teacher</b>, because <i>times that suit you both</i> is
   * a question about two parties and the free/busy door takes one teacher. A
   * co-taught night is a different question, and offering a grid built from
   * half the room would be worse than offering none.
   */
  const soloDuetTrio = dancerIds.length > 0 && dancerIds.length <= 3
  const oneTeacher = teachingStaffIds.length === 1

  const lengthMinutes =
    from && to ? Math.max(0, toMinutes(to) - toMinutes(from)) : 0

  const canSuggest = askable && soloDuetTrio && oneTeacher && lengthMinutes > 0

  /*
   * <b>A pick belongs to the placement it was made for</b> (D-893).
   *
   * <b>Kept with its placement rather than on its own, and an audit is why.</b>
   * A bare <c>pickedRoom</c> outlives the question it answered: choose Studio B
   * for Monday, then change to a day whose calendar cannot be read — the
   * ordinary ~75 seconds after a deploy (D-33) — and the panel said <i>moving it
   * keeps Studio A</i> while the request carried <b>Studio B</b>, with the
   * picker hidden so the choice could be neither seen nor undone. A sentence
   * stating the opposite of what it does, on a move that emails every family.
   *
   * <b>Derived during render rather than cleared in an effect</b>, because an
   * effect runs after the paint that has already used the stale value.
   */
  const placement = `${landing}|${from}|${to}`

  /** Null is *keep the room it is in*, which is what the server reads a null as. */
  const [pick, setPick] = useState<{ at: string; roomId: string | null }>({
    at: placement,
    roomId: null,
  })

  const pickedRoom = pick.at === placement ? pick.roomId : null

  const changes: { date?: string; startTime?: string; endTime?: string; roomId?: string } = {}
  if (day && day !== was.day) changes.date = day
  if (start && start !== was.start) changes.startTime = start
  if (end && end !== was.end) changes.endTime = end

  /*
   * <b>A room sent unchanged is the same statement as sending none</b> (D-893),
   * and the picker reports the room it already meets in as an ordinary pick
   * (D-896) — so keeping it has to be recognised here rather than read as a
   * change nobody asked for.
   */
  if (pickedRoom && pickedRoom !== roomId) changes.roomId = pickedRoom

  const nothingToDo = Object.keys(changes).length === 0

  // The one refusal worth making here rather than at the server: both fields
  // are on screen, so the answer is already in front of her.
  const backwards = Boolean(start) && Boolean(end) && end <= start

  /*
   * <b>This year and next</b> (D-701), `asked`: *"we should prob just lock the
   * date picker to this year and this year plus one no?"*
   *
   * <b>The far end is the one that bites.</b> A date input takes a four-digit
   * year, so 2027 for 2026 is one keystroke — and it passes the *already
   * meets* check, passes the clash check, and emails every family in the class
   * that their Tuesday has moved to next year. The near end the server already
   * refuses.
   *
   * <b>Enforced, not merely declared.</b> `max` on a date input makes a value
   * *invalid*; outside a form with validation it stops nothing, and this panel
   * is not a form. So the attribute is there for the picker's own greying-out
   * and this is what actually holds the button.
   *
   * <b>A client rule with no matching refusal, deliberately</b> (D-482): the
   * server has no upper bound and a season is the honest one, which it would
   * have to know about. What this stops is the typo, and it stops it where the
   * typo happens.
   */
  const thisYear = Number(today().slice(0, 4))
  const lastYear = thisYear + 1
  const lastDay = `${lastYear}-12-31`
  const tooFar = beyondNextYear(day, today())

  const move = useMutation({
    mutationFn: () => moveMyClassNight(classId, occurrenceId, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roster'] })
      queryClient.invalidateQueries({ queryKey: ['calendar'] })
      queryClient.invalidateQueries({ queryKey: ['conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['teacher'] })

      // The move changes who holds which room, so the day this panel just read
      // to answer that is now wrong (D-893).
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
    },
  })

  /*
   * <b>44px, like everything else she taps on a phone.</b> `observed` at
   * 390px: borrowed from the counter-offer's controls these rendered
   * <b>34px</b> against 44 for the two buttons directly beneath them, and a
   * date input is a *finger* target - its value, its stepper arrows and its
   * calendar button all live inside that height. The layout sweep cannot find
   * this: it fails under 24px, so 34 reports clean.
   */
  const field =
    'min-h-[44px] rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-accent'

  if (move.isSuccess) {
    /*
     * <b>Both, because either alone can be the thing she changed.</b> Naming
     * only the day read *"Moved to Tuesday 23 September"* to a teacher who had
     * moved 4:00 to 5:00 and left the day alone - a true sentence that says
     * nothing about what she just did.
     */
    const nowStart = changes.startTime ?? was.start

    /*
     * Named only when she changed it, by the same argument as the day and the
     * hour above: a room she did not touch is not what she just did (D-893).
     *
     * <b>The name comes from the panel's own prop now</b> (D-896). It used to
     * be looked up in this panel's copy of the availability list, and that list
     * is the thing the shared picker replaced — so the receipt says *a
     * different room* rather than naming one the panel can no longer resolve.
     */
    const movedRoom = changes.roomId !== undefined

    return (
      <div className="mb-3 rounded-xl border border-line bg-surface-2 px-3 py-3">
        <p role="status" className="text-[12.5px] text-good">
          Moved to {formatFullDate(changes.date ?? day)}
          {nowStart && (
            <>
              , {formatTime(nowStart)}
              {meridiem(nowStart)}
            </>
          )}
          {movedRoom && <>, in a different room</>}. Everyone in the class has been told.
        </p>

        <button
          type="button"
          onClick={onDone}
          className="mt-2 min-h-[44px] rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
        >
          Done
        </button>
      </div>
    )
  }

  return (
    <div className="mb-3 rounded-xl border border-line bg-surface-2 px-3 py-3">
      <p className="text-[11px] font-bold tracking-[0.09em] text-ink-3 uppercase">Move this night</p>

      {/*
        <b>Said before she fills the form in, not after she has moved it.</b>
        The pattern is `ManageClasses` and this is not — so a teacher who means
        "we meet on Wednesdays now" has to ask the studio, and finding that out
        from a class that went back to Tuesday the following week would be the
        worst way to learn it.
      */}
      <p className="mt-1 text-[12px] text-ink-3">
        This one night only &mdash; the rest of the season stays where it is.
      </p>

      {/*
        <b>Times that suit everybody, for a solo, duet or trio</b> (D-898), the
        same assistant the booking form opens with.

        <b>Above the fields rather than instead of them.</b> A teacher moving a
        Tuesday to the following Tuesday knows the answer already and should not
        have to read a grid to type it; this is for the case the owner named,
        where a dancer cannot make it and the question is when she can.
      */}
      {canSuggest && (
        <div className="mt-3">
          <SchedulingAssistant
            dancerIds={dancerIds}
            teacherStaffId={teachingStaffIds[0]}
            minutes={lengthMinutes}
            picked={{ date: landing, start: `${from}:00` }}
            teacherLabel="Teacher"
            onPick={(pickedDate, pickedStart, pickedEnd) => {
              /*
               * <b>Sliced back to `HH:mm`</b>, which is what a `<input
               * type="time">` holds and what `changes` compares against. The
               * assistant speaks the feed's `HH:mm:ss`, and the booking form
               * has already paid once for appending rather than converting
               * between the two.
               */
              setDay(pickedDate)
              setStart(pickedStart.slice(0, 5))
              setEnd(pickedEnd.slice(0, 5))
            }}
          />
        </div>
      )}

      <div className="mt-2.5 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-[11.5px] font-semibold text-ink-3">
          Day
          <input
            type="date"
            value={day}
            min={today()}
            max={lastDay}
            onChange={(e) => setDay(e.target.value)}
            className={field}
          />
        </label>

        <label className="flex flex-col gap-1 text-[11.5px] font-semibold text-ink-3">
          From
          <input
            type="time"
            value={start}
            step={300}
            onChange={(e) => {
              setStart(e.target.value)

              // The class keeps its length when she drags the start, which is
              // what she means nine times in ten (D-700).
              const shifted = shiftEnd(start, end, e.target.value)
              if (shifted) setEnd(shifted)
            }}
            className={field}
          />
        </label>

        <label className="flex flex-col gap-1 text-[11.5px] font-semibold text-ink-3">
          To
          <input
            type="time"
            value={end}
            step={300}
            onChange={(e) => setEnd(e.target.value)}
            className={field}
          />
        </label>
      </div>

      {backwards && (
        <p className="mt-2 text-[12px] text-crit">The finish has to be after the start.</p>
      )}

      {/*
        <b>The rule the code actually enforces, in the units it enforces it
        in.</b> This read *"within this season or the next one"*, which is a
        different rule: the season runs September to June, so it called
        2028-03-10 refused when it is allowed, and said nothing about
        2027-08-15, which is in neither season and is accepted. Named by an
        audit — and it is the only sentence here a teacher ever reads, so it is
        the one that has to be true. Calendar years are also the owner's own
        words: *"this year and this year plus one"*.
      */}
      {tooFar && (
        <p className="mt-2 text-[12px] text-crit">
          A night can only be moved into {thisYear} or {lastYear}. Check the year.
        </p>
      )}

      {/*
        <b>The rooms, through the picker the private request uses</b> (D-896).
        <b>Only where there is a room to keep</b> - a class with no room stays
        roomless when it moves, which is what it did before; giving one a room
        is a different act and belongs where rooms are arranged.
      */}
      {askable && roomId && (
        <div className="mt-2.5">
          <p className="text-[11.5px] font-semibold text-ink-3">Room</p>

          <div className="mt-1">
            <RoomPicker
              date={landing}
              start={from}
              end={to}
              roomId={pickedRoom ?? ''}
              onPick={(id) => setPick({ at: placement, roomId: id })}
              currentRoomId={roomId}
              excludeOccurrenceId={occurrenceId}
              variant="chips"
            />
          </div>
        </div>
      )}

      <p className="mt-2.5 text-[12px] text-ink-3">
        Everyone in the class is emailed where and when it now meets.
      </p>

      <div className="mt-2.5 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={nothingToDo || backwards || tooFar || move.isPending}
          onClick={() => move.mutate()}
          className="min-h-[44px] rounded-lg bg-accent px-3.5 text-[13px] font-semibold text-on-accent disabled:opacity-40"
        >
          {move.isPending ? 'Moving...' : 'Move it'}
        </button>

        <button
          type="button"
          onClick={onDone}
          className="min-h-[44px] rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
        >
          Cancel
        </button>
      </div>

      {/*
        The server's own sentence, never a status code. Every refusal this
        route makes is the only thing that can explain why the move did not
        happen - *"this class already meets on Wed 8 Oct"*, *"that night is
        cancelled"*, *"that room is already taken at that time"* - and each one
        names something she can act on.
      */}
      {move.isError && (
        <p role="alert" className="mt-2 text-[12px] text-crit">
          {(move.error as Error).message}
        </p>
      )}
    </div>
  )
}

/**
 * Is this day past the end of next year (D-701)?
 *
 * <b>Exported so it can be tested at all.</b> The value that matters is a
 * five-digit year, and jsdom's date input refuses to hold one — `fireEvent`
 * leaves the field empty, so driving the widget cannot reach the case the
 * check exists for. `MoveRoomPanel` exports `recurs` for the same reason.
 *
 * <b>Split on the dash, not sliced to four characters — and both of the
 * obvious spellings are wrong on the same input.</b> A date input's year
 * segment really does accept five digits (the spec allows up to 275760), and
 * `10000-01-01` defeats each in turn: compared as strings it sorts *below*
 * `2027-12-31`, because `'1'` precedes `'2'`; sliced to four characters it
 * parses as the year **1000**, which is below the bound for the same reason
 * one digit further on. The first was found by an audit and the second by this
 * function's own test, written for the first.
 *
 * <b>Blank is not too far.</b> A half-typed field is somebody mid-edit, and
 * the button is already held by there being nothing to do.
 */
export function beyondNextYear(day: string, todayIso: string): boolean {
  if (!day) return false

  return Number(day.split('-')[0]) > Number(todayIso.split('-')[0]) + 1
}

/**
 * Has this night already begun (D-704)?
 *
 * <b>The server's rule, mirrored so the button is not offered to be refused.</b>
 * A night on an earlier day has begun; a night today has begun once its start
 * time is at or before now - `<=`, as the server has it, because at the start
 * families are walking in. A night with no start time never has one to pass.
 *
 * <b>The server stays the authority.</b> This reads the device's own clock, and
 * a phone set to another time zone can disagree for a few hours - in which case
 * the button shows and the move is refused with the server's sentence, which is
 * the safe direction to be wrong in.
 *
 * <b>Exported so the time of day can be tested at a time of day.</b> Driven
 * through the roster, "later today" is not later at 11:58pm.
 */
export function hasStarted(
  date: string,
  startTime: string | null,
  todayIso: string,
  nowMinutes: number,
): boolean {
  if (date < todayIso) return true
  if (date > todayIso || !startTime) return false

  const [h, m] = startTime.split(':').map(Number)
  return h * 60 + m <= nowMinutes
}
